/**
 * Página de Login
 * Formulário de autenticação do sistema usando Ant Design
 */

import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Form, Input, Button, Alert } from "antd";
import { IdcardOutlined, LockOutlined } from "@ant-design/icons";
import { useAuth } from "../hooks/useAuth";
import { resolvePostLoginPath } from "../lib/defaultHomeRoute";
import { useTenant } from "../hooks/useTenant";
import { formatCPF } from "../lib/utils";
import InvalidSubdomain from "./InvalidSubdomain";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    isAuthenticated,
    loading,
    error: authError,
    granularPermissionsEnabled,
    hasEffectivePermission,
  } = useAuth();
  const { tenant, isEducapaco, isNTME, isValid } = useTenant();
  const [form] = Form.useForm();

  // Se já estiver autenticado, redireciona
  useEffect(() => {
    if (isAuthenticated) {
      const target = resolvePostLoginPath({
        granularPermissionsEnabled,
        hasEffectivePermission,
        preferredPath: location.state?.from?.pathname,
      });
      navigate(target, { replace: true });
    }
  }, [
    isAuthenticated,
    navigate,
    location.state?.from?.pathname,
    granularPermissionsEnabled,
    hasEffectivePermission,
  ]);

  // Se o subdomínio não for válido, mostra página de erro
  if (!isValid) {
    return <InvalidSubdomain />;
  }

  /**
   * Handle do submit do formulário
   */
  const handleSubmit = async values => {
    try {
      // Remove formatação antes de enviar (apenas números)
      const cpfClean = values.cpf?.replace(/\D/g, "") || "";
      await login(cpfClean, values.password);
    } catch (err) {
      // A mensagem de erro já vem traduzida do AuthContext
      // Se for erro de tenant mismatch, a mensagem já vem formatada
      const errorMessage = err.message || "Erro ao fazer login. Tente novamente.";
      form.setFields([
        {
          name: "submit",
          errors: [errorMessage],
        },
      ]);
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background com imagem, blur e opacidade */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "url('/assets/bg_login.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "blur(4px)",
          transform: "scale(1.1)",
        }}
      />
      {/* Overlay com opacidade - muda cor baseado no tenant */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isEducapaco
            ? "rgba(240, 242, 245, 0.7)"
            : "rgba(30, 64, 175, 0.7)", // Azul para NTME
        }}
      />

      {/* Conteúdo */}
      <div style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: "500px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
        <Card
          style={{
            width: "100%",
            boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
          }}
          bodyStyle={{
            paddingTop: "24px",
            paddingBottom: "24px",
            paddingLeft: "24px",
            paddingRight: "24px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "8px",
              flexDirection: "column",
            }}
          >
            {/* Logo baseado no tenant */}
            <img
              src={isNTME ? "/assets/logo-ntme.png" : "/assets/logo-educapaco.png"}
              alt={isNTME ? "NTME" : "EducaPaço"}
              style={{
                maxWidth: "320px",
                width: "100%",
                height: "auto",
                display: "block",
                ...(isNTME && { paddingTop: "10px", paddingBottom: "10px" }),
              }}
            />
          </div>

          {authError && (
            <Alert message={authError} type="error" showIcon style={{ marginBottom: 24 }} />
          )}

          <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
            <Form.Item
              label="CPF"
              name="cpf"
              rules={[
                { required: true, message: "CPF é obrigatório" },
                {
                  validator: (_, value) => {
                    const cleaned = value?.replace(/\D/g, "") || "";
                    if (cleaned.length !== 11) {
                      return Promise.reject(new Error("CPF deve conter 11 dígitos"));
                    }
                    return Promise.resolve();
                  },
                  transform: (value) => value?.replace(/\D/g, ""),
                },
              ]}
            >
              <Input
                prefix={<IdcardOutlined />}
                placeholder="000.000.000-00"
                size="large"
                maxLength={14}
                onChange={(e) => {
                  // Remove caracteres não numéricos
                  const cleaned = e.target.value.replace(/\D/g, "");
                  // Limita a 11 dígitos
                  const limited = cleaned.slice(0, 11);
                  // Formata com máscara progressiva para exibição
                  const formatted = formatCPF(limited);
                  form.setFieldsValue({ cpf: formatted });
                }}
              />
            </Form.Item>

            <Form.Item
              label="Senha"
              name="password"
              rules={[
                { required: true, message: "Senha é obrigatória" },
                { min: 6, message: "Senha deve ter no mínimo 6 caracteres" },
              ]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="••••••••" size="large" />
            </Form.Item>

            <p style={{ fontSize: 14, color: "#666", textAlign: "center", marginBottom: 24 }}>
              Esqueceu sua senha? Entre em contato com o suporte da Secretaria para atualizar seu
              acesso.
            </p>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                style={isNTME ? {
                  background: "#1e40af",
                  borderColor: "#1e40af",
                } : undefined}
              >
                Entrar
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default Login;
